import React from "react";
import { CalendarCheck, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatAttendanceDate } from "../lib/format";

interface AttendanceCardProps {
  dateStr: string;
  compact: boolean;
  isLoading: boolean;
  onValidate: (d: string) => void;
  onClear: (d: string) => void;
}

/**
 * Action-required card prompting the user to confirm (or clear) an
 * `unconfirmed` Office day that has already passed. Lives in the
 * notification bell even though the underlying state is in `AppContext`
 * (in-memory), not the `notifications` table — the bell surfaces the
 * prompt at the right moment regardless of where the state is held.
 */
export function AttendanceCard({
  dateStr,
  compact,
  isLoading,
  onValidate,
  onClear,
}: AttendanceCardProps) {
  return (
    <div
      className={cn(
        "transition-opacity",
        compact ? "px-4 py-3" : "px-5 py-4",
        isLoading && "opacity-40 pointer-events-none",
      )}
    >
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <CalendarCheck className="h-4 w-4 text-violet-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              "font-semibold leading-tight",
              compact ? "text-xs" : "text-sm",
            )}
          >
            Confirm your attendance
          </p>
          <p
            className={cn(
              "text-muted-foreground mt-0.5 leading-relaxed",
              compact ? "text-[11px]" : "text-xs",
            )}
          >
            {formatAttendanceDate(dateStr)}
          </p>
          <div className="flex gap-1.5 mt-2.5">
            <Button
              size="sm"
              className={cn(
                "gap-1 font-medium",
                compact ? "h-6 text-[10px] px-2" : "h-7 text-xs px-3",
              )}
              disabled={isLoading}
              onClick={() => onValidate(dateStr)}
            >
              <CheckCircle2 className="h-3 w-3" />
              Yes, I went
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={cn(
                "gap-1 font-medium text-muted-foreground hover:text-foreground",
                compact ? "h-6 text-[10px] px-2" : "h-7 text-xs px-3",
              )}
              disabled={isLoading}
              onClick={() => onClear(dateStr)}
            >
              <XCircle className="h-3 w-3" />
              No, clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
